'use client';

import { useMemo, useState, type JSX } from 'react';

import clsx from 'clsx';

import { SHIMMER_CLASSES } from 'DesignSystem2/Skeleton/constants';
import { Body } from 'DesignSystem3/Text/Body';
import type { KpowHistoryPoint, KpowScore, KpowSnapshot } from 'react_pages/AmericanPowerIndex/api/kpowApi';
import { copyToClipboard } from 'utils/social';

import { kpowAnnotations } from '../kpowAnnotations';
import { partyColorBySign } from '../kpowColors';
import {
    DAY_MS,
    formatDelta,
    formatScoreCompact,
    pointAtAge,
    pointBefore,
    windowPointsByPeriod,
    type KpowPeriod,
} from '../kpowFormat';
import ChartStats from './ChartStats';
import IndexGauge from './IndexGauge';
import IndexGraph from './IndexGraph';
import KpowPeriodTabs from './KpowPeriodTabs';

interface ChartCardProps {
    snapshot: KpowSnapshot | null;
    points: KpowHistoryPoint[];
    /** History query still on its first fetch: show a shimmer rather than the empty state. */
    loading: boolean;
    /** When true, chart area grows to fill the parent container instead of using a fixed height. */
    flexible?: boolean;
}

/** Upload/share glyph (up arrow out of a tray). Inherits the button's text color via currentColor. */
function ShareIcon(): JSX.Element {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
                d="M10 2.5v8.5M6.75 5.25 10 2l3.25 3.25"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M5.5 8.5H4.5A1.5 1.5 0 0 0 3 10v6a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 17 16v-6a1.5 1.5 0 0 0-1.5-1.5h-1"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/** Share the KPOW page via the native share sheet, falling back to copying the URL. */
const shareKpow = (): void => {
    if (typeof window === 'undefined') return;
    const url = window.location.href;
    if (navigator.share) {
        void navigator.share({ title: 'KPOW: American Power Index', url }).catch(() => undefined);
        return;
    }
    copyToClipboard(url);
};

/** "Updated 2 min ago" freshness label from the snapshot's data_as_of (unix seconds). */
const formatUpdatedAgo = (dataAsOf: number): string => {
    const mins = Math.max(0, Math.round((Date.now() - dataAsOf * 1000) / 60_000));
    if (mins < 1) return 'Updated just now';
    if (mins < 60) return `Updated ${mins} min ago`;
    const hrs = Math.round(mins / 60);
    return `Updated ${hrs} ${hrs === 1 ? 'hour' : 'hours'} ago`;
};

/** Chart-area placeholder: shimmer while the first fetch is in flight, empty copy once it settles with no data. */
const ChartPlaceholder = ({ loading }: { loading: boolean }): JSX.Element =>
    loading ? (
        <div className={clsx(SHIMMER_CLASSES, 'h-full w-full rounded-x20 sm:rounded-[0]')} />
    ) : (
        <div className="h-full w-full flex items-center justify-center">
            <Body.x20 className="text-text-x30">No data yet.</Body.x20>
        </div>
    );

/**
 * Headline value at display size: the real score (black, party carried in the "+2.90 R" text),
 * a shimmer while loading, or "–" once settled with no data. Fixed 58px line so the row never
 * shifts as the value scrubs.
 */
const HeaderValue = ({ display, loading }: { display: KpowScore | null; loading: boolean }): JSX.Element => {
    const valueClasses = 'font-kalshi-sans text-[54px] leading-[58px] tracking-[-1.62px] text-text-x10 tabular-nums';
    if (display !== null) {
        return <h2 className={valueClasses}>{formatScoreCompact(display)}</h2>;
    }
    if (loading) {
        return (
            <div className="flex h-[58px] items-center">
                <div className={clsx(SHIMMER_CLASSES, 'h-[40px] w-[180px] rounded-x10')} />
            </div>
        );
    }
    return <h2 className={valueClasses}>–</h2>;
};

/**
 * 24h delta beside the value ("+0.6 R Last 24H"), colored by the direction it moved. Hidden while
 * scrubbing the chart (the value follows the cursor, but a "last 24h" delta would be meaningless).
 */
const HeaderDelta = ({
    dayChange,
    dayDeltaColor,
    loading,
    scrubbing,
}: {
    dayChange: number | null;
    dayDeltaColor?: string;
    loading: boolean;
    scrubbing: boolean;
}): JSX.Element | null => {
    if (loading) {
        return (
            <div className="flex h-[18px] items-center">
                <div className={clsx(SHIMMER_CLASSES, 'h-[14px] w-[120px] rounded-x10')} />
            </div>
        );
    }
    if (dayChange === null || scrubbing) return null;
    return (
        <span
            className={clsx(
                'font-kalshi-sans text-[25px] leading-[18px] tabular-nums whitespace-nowrap',
                !dayDeltaColor && 'text-text-x20',
            )}
            style={dayDeltaColor ? { color: dayDeltaColor } : undefined}
        >
            {`${formatDelta(dayChange)} Last 24H`}
        </span>
    );
};

const ChartCard = ({ snapshot, points, loading, flexible = false }: ChartCardProps): JSX.Element => {
    // Hover state is owned here so the headline (value / lean / 24h delta) can scrub
    // with the cursor. When idle, we anchor to the live snapshot. The caller appends
    // the snapshot as the rightmost point so scrubbing to the right edge agrees with
    // the idle headline.
    const [hoverPoint, setHoverPoint] = useState<KpowHistoryPoint | null>(null);
    const [period, setPeriod] = useState<KpowPeriod>('All');

    const composite = snapshot?.composite ?? null;
    // KpowScore and KpowHistoryPoint share signed_value / display_party / display_value.
    const display = hoverPoint ?? composite;

    // Delta re-anchors to the hovered moment so "Last 24H" stays internally consistent;
    // when idle it's the latest snapshot vs. the point ~24h ago.
    const baseline = hoverPoint ? pointBefore(points, hoverPoint.end_period_ts, DAY_MS) : pointAtAge(points, DAY_MS);
    const dayChange = display !== null && baseline !== null ? display.signed_value - baseline.signed_value : null;
    const dayDeltaColor = dayChange !== null ? partyColorBySign(dayChange) : undefined;

    // The footer stats read the full series; only the chart line is windowed by period.
    const visiblePoints = useMemo(() => windowPointsByPeriod(points, period), [points, period]);

    // Freshness label for the tab row, anchored to the snapshot's data_as_of.
    const updatedLabel = snapshot ? formatUpdatedAgo(snapshot.data_as_of) : null;

    return (
        // sm: (phone only) the card bleeds out by the shell's 16px gutter so the chart takes the
        // full width. Uses calc(100% + 32px) + -mx-2 (layout-width based, NOT 100vw) so it can't
        // overshoot into the scrollbar and trigger horizontal scroll. Card drops its horizontal
        // padding on phones so the chart line fills the width; the header + footer re-add the gutter.
        // Tablets keep the carded layout (matches the repo convention of stacking only at sm:).
        <div
            className={clsx(
                'flex flex-col gap-2 w-full min-w-0 p-3',
                // Embed (flexible) renders edge-to-edge with no card chrome -- the embedder
                // adds rounding/borders/styling. The main-app page keeps the bordered card.
                flexible
                    ? 'h-full'
                    : 'bg-container-x40 border border-solid border-stroke-x40 rounded-x40 sm:rounded-[0] sm:border-l-0 sm:border-r-0 sm:-mx-2 sm:w-[calc(100%+32px)] sm:px-0',
            )}
        >
            {/* Header: KPOW + American Power Index with a Share action, then the display-size
                value and its 24h delta. Fixed line heights keep the row from shifting as the value
                scrubs with the cursor. */}
            <div className="flex flex-col gap-2 sm:px-3">
                <div className="flex flex-col gap-1">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-baseline gap-1">
                            <span className="font-kalshi-sans text-[25px] font-semibold leading-[24px] text-text-x10">
                                KPOW
                            </span>
                            <span className="truncate font-kalshi-sans text-[14px] leading-[24px] text-text-x20">
                                American Power Index
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={shareKpow}
                            aria-label="Share the American Power Index"
                            className="flex shrink-0 cursor-pointer items-center gap-1 border-none bg-transparent text-text-x20 transition-colors hover:text-text-x10"
                        >
                            <ShareIcon />
                            <span className="font-kalshi-sans text-[15px] font-medium leading-[22px]">Share</span>
                        </button>
                    </div>
                    {/* wrap so the nowrap "Last 24H" delta drops below the big value on narrow phones
                        instead of pushing the row past the viewport. */}
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <HeaderValue display={display} loading={loading} />
                        <HeaderDelta
                            dayChange={dayChange}
                            dayDeltaColor={dayDeltaColor}
                            loading={loading}
                            scrubbing={hoverPoint !== null}
                        />
                    </div>
                </div>

                {/* Flat time-range tabs, capped by a strong divider that frames the top of the chart;
                    freshness label pinned to the right. */}
                <div className="flex items-center justify-between gap-3 border-b border-solid border-stroke-x10 pt-1">
                    <KpowPeriodTabs value={period} onChange={setPeriod} />
                    {updatedLabel ? (
                        <Body.x10 className="shrink-0 whitespace-nowrap text-text-x20">{updatedLabel}</Body.x10>
                    ) : null}
                </div>
            </div>

            {/* Chart, taller on mobile so the line doesn't read squat against the full
                viewport width. Matches the Figma mobile chart-visual height (~339px). */}
            <div className={flexible ? 'flex-1 min-h-0 w-full' : 'h-[400px] w-full sm:h-[420px]'}>
                {points.length > 0 ? (
                    <IndexGraph
                        points={visiblePoints}
                        period={period}
                        annotations={kpowAnnotations}
                        onHoverPoint={setHoverPoint}
                    />
                ) : (
                    <ChartPlaceholder loading={loading} />
                )}
            </div>

            {/* Footer: 2x2 stat grid (full series, period-independent). */}
            <div className="sm:px-3">
                <ChartStats points={points} loading={loading} />
            </div>

            {/* Republican/Democrat lean gauge, part of the same card. Scrubs with the chart. */}
            <div className="pt-1 sm:px-3">
                <IndexGauge composite={display} />
            </div>
        </div>
    );
};

export default ChartCard;
